import {Component, Inject} from '@angular/core';
import {FormBuilder, FormGroup, FormArray, Validators, AbstractControl} from '@angular/forms';
import {AdminService} from '../../services/admin.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {SubCategory} from "../../../shared/SharedEntities";
import Swal from "sweetalert2";

@Component({
  selector: 'app-category-dialog',
  templateUrl: './category-dialog.component.html',
  styleUrls: ['./category-dialog.component.scss']
})
export class CategoryDialogComponent {
  categoryForm: FormGroup;
  isEditMode: boolean = false;
  selectedReorderTargetId: string = '';

  constructor(
    private fb: FormBuilder,
    private adminService: AdminService,
    public dialogRef: MatDialogRef<CategoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { category?: SubCategory }
  ) {
    this.isEditMode = !!data.category;

    this.categoryForm = this.fb.group({
      name: [data.category ? data.category.name : '', Validators.required],
      sortOrder: [data.category ? data.category.sortOrder : '', Validators.required],
      subCategories: this.fb.array<FormGroup>([])
    });

    if (data.category && data.category.subCategories) {
      this.setSubCategories(data.category.subCategories);
    }
  }

  get subCategories(): FormArray<FormGroup> {
    return this.categoryForm.get('subCategories') as FormArray<FormGroup>;
  }

  setSubCategories(subCategories: SubCategory[]) {
    subCategories.forEach(subCat => {
      const subCategoryGroup = this.fb.group({
        id: [subCat.id],
        name: [subCat.name, Validators.required],
        sortOrder: [subCat.sortOrder],
        subCategories: this.buildNestedSubCategories(subCat.subCategories || [])
      });
      this.subCategories.push(subCategoryGroup);
    });
  }

  buildNestedSubCategories(subCategories: SubCategory[]): FormArray<FormGroup> {
    const nestedSubCategoriesArray = this.fb.array<FormGroup>([]);
    subCategories.forEach(subCat => {
      const subCategoryGroup = this.fb.group({
        id: [subCat.id],
        name: [subCat.name, Validators.required],
        sortOrder: [subCat.sortOrder],
        subCategories: this.buildNestedSubCategories(subCat.subCategories || [])
      });
      nestedSubCategoriesArray.push(subCategoryGroup);
    });
    return nestedSubCategoriesArray;
  }

  addSubCategory(subCategoriesArray: FormArray = this.subCategories): void {
    const newSubCategoryGroup = this.fb.group({
      name: ['', Validators.required],
      sortOrder: ['', Validators.required],
      subCategories: this.fb.array<FormGroup>([])
    });
    subCategoriesArray.push(newSubCategoryGroup);
  }

  removeSubCategory(subCategoriesArray: FormArray, index: number): void {
    subCategoriesArray.removeAt(index);
  }

  asFormArray(control: AbstractControl | null): FormArray {
    return control as FormArray;
  }

  onSave(): void {
      const categoryData = this.categoryForm.getRawValue();
      const isGaratCategory =
        this.isEditMode && this.data.category?.name === 'GARAT';

      const proceedUpdate = () => {
        this.adminService.updateCategory(this.data.category!.id!, categoryData).subscribe(() => {
          this.dialogRef.close(true);
        });
      };

      if (isGaratCategory) {
        Swal.fire({
          title: 'A je i sigurt?',
          text: 'Ju duhet të përditësoni emrin në Ligë përpara kësaj!',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Po, përditësoje!',
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            proceedUpdate();
          }
        });
      } else if (this.isEditMode) {
        proceedUpdate();
      } else {
        this.adminService.createCategory(categoryData).subscribe(() => {
          this.dialogRef.close(true);
        });
      }
  }

  onSortOrder(): void {
    const subCategoryWithChildren = this.subCategories.controls.find(
      control => this.asFormArray(control.get('subCategories')).length > 0
    );

    const subCategoryId = subCategoryWithChildren!.get('id')?.value;
    this.selectedReorderTargetId = subCategoryId;

    const payload = this.buildReorderPayloadFor(subCategoryId);

    if (!payload || payload.length === 0) {
      Swal.fire('Gabim', 'Nuk u gjetën nënkategori për renditje.', 'error');
      return;
    }

    this.adminService.updateCategoryOrder(subCategoryId, payload).subscribe({
      next: () => {
        Swal.fire('Sukses', 'Renditja u përditësua me sukses!', 'success');
        this.dialogRef.close(true);
      },
      error: () => {
        Swal.fire('Gabim', 'Diçka shkoi keq gjatë përditësimit të renditjes.', 'error');
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getDialogTitle(): string {
    return this.isEditMode ? 'Edit Category' : 'Create Category';
  }

  private buildReorderPayloadFor(targetSubCategoryId: string): any[] | null {
    const formValue = this.categoryForm.getRawValue();

    const target = formValue.subCategories.find((sc: any) => sc.id === targetSubCategoryId);
    if (!target) return null;

    return (target.subCategories || []).map((child: any) => ({
      id: child.id,
      name: child.name,
      referenceLeagueId: child.referenceLeagueId || null,
      sortOrder: child.sortOrder,
      subCategories: []
    }));
  }

}
